import React, { useMemo, useState } from 'react'

import { useT } from '../../i18n'
import type { DatasetColumn } from '../../utils/reportDatasets'
import { columnKind, COLUMN_KIND_ICON, moveColumn, moveColumnTo, offeredColumns, orderedReportColumns } from '../../utils/reportColumns'

// Findings #221: two lists instead of one wall of checkboxes. The right list
// is the report's column order; `selected` keeps that order when it is saved.

const listStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 8,
  maxHeight: 280,
  overflow: 'auto',
  display: 'grid',
  gap: 4,
  alignContent: 'start',
  WebkitOverflowScrolling: 'touch'
}

const headingStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }
const iconStyle: React.CSSProperties = { color: '#64748b', fontSize: 12, minWidth: 26, textAlign: 'center' }
const moveButtonStyle: React.CSSProperties = { minWidth: 34, minHeight: 34, padding: 0 }

const ColumnIcon = ({ column }: { column: DatasetColumn }) => {
  const t = useT()
  const kind = columnKind(column)
  return <span title={t(`reports.columnKind.${kind}`)} style={iconStyle}>{COLUMN_KIND_ICON[kind]}</span>
}

export type ReportColumnPickerProps = {
  columns: DatasetColumn[]
  selected: string[]
  onChange: (next: string[]) => void
}

export default function ReportColumnPicker({ columns, selected, onChange }: ReportColumnPickerProps) {
  const t = useT()
  const [search, setSearch] = useState('')
  const [dragged, setDragged] = useState<string | null>(null)

  // Internal keys of an old report are not listed here.
  const chosen = useMemo(() => orderedReportColumns(columns, selected), [columns, selected])
  const chosenKeys = chosen.map((column) => column.key)
  const available = useMemo(() => {
    const query = search.trim().toLowerCase()
    return offeredColumns(columns)
      .filter((column) => !selected.includes(column.key))
      .filter((column) => !query || column.label.toLowerCase().includes(query))
  }, [columns, selected, search])

  return (
    <div data-testid="report-column-picker">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <div>
          <div style={headingStyle}>{t('reports.columnPicker.available')}</div>
          <input
            type="search"
            value={search}
            placeholder={t('reports.columnPicker.search')}
            aria-label={t('reports.columnPicker.search')}
            onChange={(event) => setSearch(event.target.value)}
            style={{ width: '100%', marginBottom: 6, boxSizing: 'border-box' }}
          />
          <div style={listStyle}>
            {available.length === 0 ? (
              <div className="lead" style={{ fontSize: 12 }}>{t('reports.columnPicker.noMatch')}</div>
            ) : available.map((column) => (
              <label key={column.key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={false} onChange={() => onChange([...chosenKeys, column.key])} />
                <ColumnIcon column={column} />
                <span>{column.label}</span>
                {column.help && (
                  <span title={column.help} aria-label={column.help} style={{ color: '#64748b', cursor: 'help' }}>ⓘ</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={headingStyle}>{t('reports.columnPicker.chosen')}</div>
          <ol style={{ ...listStyle, margin: 0, listStyle: 'none' }}>
            {chosen.length === 0 ? (
              <li className="lead" style={{ fontSize: 12 }}>{t('reports.columnPicker.emptyChosen')}</li>
            ) : chosen.map((column, index) => (
              <li
                key={column.key}
                draggable
                onDragStart={() => setDragged(column.key)}
                onDragEnd={() => setDragged(null)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault()
                  if (dragged && dragged !== column.key) onChange(moveColumnTo(chosenKeys, dragged, column.key))
                  setDragged(null)
                }}
                style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'grab', opacity: dragged === column.key ? 0.5 : 1 }}
              >
                <span style={{ color: '#64748b', fontSize: 12, minWidth: 18 }}>{index + 1}.</span>
                <ColumnIcon column={column} />
                <span style={{ flex: 1, minWidth: 0 }}>{column.label}</span>
                <button
                  type="button"
                  style={moveButtonStyle}
                  disabled={index === 0}
                  aria-label={t('reports.columnPicker.moveUp', { label: column.label })}
                  onClick={() => onChange(moveColumn(chosenKeys, column.key, -1))}
                >
                  ↑
                </button>
                <button
                  type="button"
                  style={moveButtonStyle}
                  disabled={index === chosen.length - 1}
                  aria-label={t('reports.columnPicker.moveDown', { label: column.label })}
                  onClick={() => onChange(moveColumn(chosenKeys, column.key, 1))}
                >
                  ↓
                </button>
                <button
                  type="button"
                  style={moveButtonStyle}
                  aria-label={t('reports.columnPicker.remove', { label: column.label })}
                  onClick={() => onChange(chosenKeys.filter((key) => key !== column.key))}
                >
                  ✕
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="lead" style={{ fontSize: 12, marginTop: 6 }}>{t('reports.columnPicker.legend')}</div>
    </div>
  )
}
